import { Injectable } from '@angular/core';
import {
	catchError,
	EMPTY,
	expand,
	filter,
	last,
	map,
	Observable,
	of,
	Subject,
	switchMap,
	take,
	tap,
	timer,
} from 'rxjs';
import { DeviceService } from './device.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ICheckStatus } from '~models/interfaces/check-status.interface';

@Injectable({
	providedIn: 'root',
})
export class MediaplanCheckService {
	private checkStatus: Subject<ICheckStatus> = new Subject();

	private watchTrigger: Subject<boolean> = new Subject();

	constructor(
		private deviceService: DeviceService,
		private snackBar: MatSnackBar
	) {}

	public checkMediaplan(deviceUuid: string, deviceName: string): void {
		let attempt = 0;
		const maxAttempts = 60;
		const requestDelay = 3000;

		this.watchTrigger.next(true);

		const makeCheck = (): Observable<string> => {
			attempt++;
			return this.deviceService.checkMediaplan(deviceUuid).pipe(catchError(() => of('Error')));
		};

		makeCheck()
			.pipe(
				expand((result) => {
					// Если получили финальный результат - завершаем цепочку
					if (result === 'OK' || result === 'Error') {
						return EMPTY;
					}

					// Если Waiting и не превышен лимит - делаем новый запрос через 3 секунды
					if (result === 'Waiting' && attempt < maxAttempts) {
						this.checkStatus.next({ status: 'Waiting', uuid: deviceUuid, name: deviceName });
						return timer(requestDelay).pipe(switchMap(() => makeCheck()));
					}

					// Если превышен лимит
					if (result === 'Waiting' && attempt >= maxAttempts) {
						return of('MaxAttemptsReached');
					}

					return EMPTY;
				}),
				// Берем последний результат
				last(undefined, 'MaxAttemptsReached'),
				tap((finalResult) => {
					this.checkStatus.next({ status: finalResult, uuid: deviceUuid, name: deviceName });
					if (finalResult === 'OK') {
						this.snackBar.open(`Успешно обновлен медиаплан для устройства ${deviceName}`, 'Закрыть', {
							duration: 3000,
						});
					} else if (finalResult === 'Error') {
						this.snackBar.open(`Не удалось обновить медиаплан для устройства ${deviceName}`, 'Закрыть', {
							duration: 3000,
						});
					}
				}),
				map((result) => result as 'OK' | 'Error' | 'MaxAttemptsReached'),
				take(1)
			)
			.subscribe();
	}

	public getCheckStatus(uuid: string): Observable<string> {
		return this.checkStatus.asObservable().pipe(
			filter((item) => item.uuid === uuid),
			map((item) => item.status)
		);
	}

	public getActualStatuses(): Observable<ICheckStatus> {
		return this.checkStatus.asObservable();
	}

	public getWatchTrigger(): Observable<boolean> {
		return this.watchTrigger.asObservable();
	}
}
